// AllForShopCommmissions.jsx
import React, { useEffect, useState } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';

const AllForShopCommmissions = () => {
  const [msbUsers, setMsbUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLevel, setUserLevel] = useState(null);
  const [userId, setUserId] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const token = sessionStorage.getItem('token');

  useEffect(() => {
    if (!token) {
      setError('No authentication token found. Please login again.');
      setLoading(false);
      return;
    }

    try {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.userId);
      setUserLevel(decodedToken.level);

      if (decodedToken.level === 5) {
        setError('You are not allowed to view this page.');
        setLoading(false);
        return;
      } else if (decodedToken.level === 4) {
        navigate(`/msbReport/shop-commissions/msb-commissions/${decodedToken.userId}`);
        return;
      } else if (decodedToken.level <= 3) {
        fetchMsbUsers(decodedToken.userId);
      } else {
        setError('Invalid user level.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error decoding token:', error);
      setError('Invalid authentication token. Please login again.');
      setLoading(false);
    }
  }, [token, navigate]);

  const fetchMsbUsers = async (currentUserId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/users/all/${currentUserId}?level=4&status=active`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        setMsbUsers(response.data);
      } else {
        console.error('Failed to fetch MSB users');
        setError('Failed to fetch MSB users. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching MSB users:', error);
      if (error.response?.status === 403) {
        setError('You do not have permission to view MSB users.');
      } else if (error.response?.status === 404) {
        setError('No MSB users found.');
      } else {
        setError('An error occurred while fetching MSB users. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewCommissions = (msbUserId) => {
    navigate(`/msbReport/shop-commissions/msb-commissions/${msbUserId}`);
  };

  const getUserLevelText = (level) => {
    switch (level) {
      case 1: return 'Super Admin';
      case 2: return 'State Admin';
      case 3: return 'State Admin Staff';
      case 4: return 'MSB';
      case 5: return 'MSB Staff';
      default: return 'Unknown';
    }
  };

  if (error) {
    return (
      <Box sx={{ position: 'relative', padding: 3 }}>
        <BackButton />
        <Card sx={{ maxWidth: 600, margin: '0 auto', mt: 4, borderRadius: 3 }}>
          <CardContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="h6">Access Denied</Typography>
            </Alert>
            <Typography variant="body1" color="text.secondary">
              {error}
            </Typography>
            {userLevel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Your current level: {getUserLevelText(userLevel)}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', p: { xs: 2, sm: 3 } }}>
      <BackButton />

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
                MSB Users - Commission Reports
              </Typography>
              <Chip
                label={`${msbUsers.length} Users`}
                size="small"
                color="primary"
                sx={{ fontWeight: 700, borderRadius: 1.5 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Select an MSB user to view commission reports from all shops under their supervision
            </Typography>
          </Box>
          <Chip
            label={`Logged in as: ${getUserLevelText(userLevel)}`}
            variant="outlined"
            size="small"
            sx={{ fontWeight: 600, color: 'text.secondary' }}
          />
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '250px' }}>
          <CircularProgress color="primary" size={50} />
          <Typography variant="body1" sx={{ ml: 2, fontWeight: 500 }}>
            Loading MSB users...
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
          <Table aria-label="MSB Users Table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>First Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Phone Number</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {msbUsers.map((user, index) => (
                <TableRow key={user._id}>
                  <TableCell>
                    <Chip label={`#${index + 1}`} size="small" sx={{ fontWeight: 700, bgcolor: 'rgba(255, 165, 0, 0.12)', color: 'primary.dark' }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{user.firstName}</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{user.lastName}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{user.phoneNumber}</TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', maxWidth: 220 }}>{user.address}</TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleViewCommissions(user._id)}
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                    >
                      View Commissions
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {msbUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No MSB users found.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      There are currently no active MSB users in the system.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AllForShopCommmissions;